import React, { Fragment, useContext, useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

/* Components */
import ProductOverviewProduct from './ProductOverviewProduct';

/* Common */
import CardProductLoading from '~/components/common/cards/CardProductLoading';
import ProductOverviewBanner from './ProductOverviewBanner';
import { Icon } from '~/components/common';

/* Constants */
import { fadeInOut } from '~/constants/animations';
import { StaticData } from '../../../context/staticData';

/* Icons */
import KlantenVertellenIcon from '~/../symbols/klantenvertellen.svg';
import StarFillIcon from '~/../symbols/star-fill.svg';
import StarHalfIcon from '~/../symbols/star-half.svg';
import StarBorderIcon from '~/../symbols/star-border.svg';

export default function ProductOverviewResults({
    items,
    favorites,
    isLoading,
    loadingElements = 48,
    rowClasses = `row row--md-equalized gutters-0 gutters-md-4 overflow-hidden row--bordered row--md-unbordered`,
    colClasses = 'col-12 col-md-6 col-xl-4 col-xxl-3 col-xxxl-3',
    stickerTooltips,
    review
}) {
    loadingElements = Array.from(Array(loadingElements).keys());
    const [results, setResults] = useState([]);
    const { customOptions = {} } = useContext(StaticData) || {};
    const { banners = [] } = customOptions;

    useEffect(() => {
        updateResults();
    }, []);

    useEffect(() => {
        updateResults();
    }, [items]);

    const updateResults = () => {
        let newResults = [];
        let index = 0;

        for (let i = 0; i < items.length + 1; i++) {
            if (banners?.length > 0) {
                const banner = banners.find((banner) => banner.position - 1 === index);
                if (banner) {
                    newResults.push({ type: 'banner', data: banner });
                    index++;
                }
            }

            const item = items[i];
            if (item) {
                newResults.push({ type: 'vehicle', data: item });
            }

            index++;
        }

        setResults(newResults);
    };

    const getStars = (averageRating) => {
        const rating = averageRating ?? 0;

        const fullStars = Math.floor(rating / 2);
        const halfStars = Math.round(rating) % 2;
        const emptyStars = 5 - fullStars - halfStars;

        return (
            <span className="d-flex align-items-center text-primary">
                {[...Array(fullStars)].map((_, index) => (
                    <Icon
                        size={24}
                        key={index}
                        url={StarFillIcon.url}
                    />
                ))}
                {halfStars === 1 && <Icon
                    size={24}
                    url={StarHalfIcon.url}
                />}
                {[...Array(emptyStars)].map((_, index) => (
                    <Icon
                        size={24}
                        key={index}
                        url={StarBorderIcon.url}
                    />
                ))}
            </span>
        );
    };

    return (
        <div className={rowClasses}>
            {!isLoading ? (
                <>
                    {results.slice(0, 8).map((item, itemIndex) => {
                        return (
                            <Fragment key={itemIndex}>
                                {item.type == 'banner' && (
                                    <div className={colClasses}>
                                        <ProductOverviewBanner
                                            {...item.data}
                                        />
                                    </div>
                                )}
                                {item.type == 'vehicle' && (
                                    <div className={colClasses}>
                                        <ProductOverviewProduct
                                            vehicle={item.data}
                                            stickerTooltips={stickerTooltips}
                                            favorites={favorites}
                                        />
                                    </div>
                                )}
                            </Fragment>
                        );
                    })}

                    {(results.length > 8 && review) ? (
                        <div className="col-12">
                            <div className="my-6">
                                <div className="row gutters-2 gutters-lg-4 justify-content-center align-items-center">
                                    <div className="col-12 col-lg-auto">
                                        <div className="d-flex align-items-center justify-content-center gap-2 gap-lg-4">
                                            <Icon
                                                size={32}
                                                url={KlantenVertellenIcon.url}
                                            />
                                            <p className="text--16 text-inherit font-weight-bold">
                                                Klanten geven ons een {review.averageRating}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-12 col-lg-auto">
                                        <p className="d-flex align-items-center justify-content-center gap-1 gap-lg-2">
                                            {getStars(review.averageRating)}
                                            <span className="text--16">
                                                {review.numberOfReviews} beoordelingen
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {results.length > 8 && results.slice(8).map((item, itemIndex) => {
                        return (
                            <Fragment key={itemIndex}>
                                {item.type == 'banner' && (
                                    <div className={colClasses}>
                                        <ProductOverviewBanner
                                            {...item.data}
                                        />
                                    </div>
                                )}
                                {item.type == 'vehicle' && (
                                    <div className={colClasses}>
                                        <ProductOverviewProduct
                                            vehicle={item.data}
                                            stickerTooltips={stickerTooltips}
                                            favorites={favorites}
                                        />
                                    </div>
                                )}
                            </Fragment>
                        );
                    })}
                </>
            ) : null}
            <AnimatePresence>
                {isLoading &&
                    loadingElements.map((index) => (
                        <motion.div key={index} {...fadeInOut} className={colClasses}>
                            <CardProductLoading />
                        </motion.div>
                    ))
                }
            </AnimatePresence>
        </div>
    );
}
